/**
 * @file useCanvasBridge.ts
 * @description Мост канвы конструктора с действиями кабинета (phase19.16
 * Р2а, приёмка §7.17 п. 2): обёртка над `usePreviewBridge` для
 * `BuilderCanvas` — выбор блока, «+ блок сюда» и клик по элементу секции.
 *
 * «+ блок сюда» (`block-add`) открывает библиотеку заготовок с местом
 * вставки — тем же `openLibrary`, что строка «блок сюда» в списке блоков
 * (группа «Страница товара» у шаблона подставляется в `PresetLibraryHost`
 * по виду страницы). Только в черновике, без чужого источника на канве и
 * с правом на контент; на полной странице — подсказка вместо окна.
 *
 * Клик по элементу секции (`element-select { path }`, путь уже через точку):
 * блок выбирается справа и открывается панель элемента. Id элемента — из
 * несохранённой формы этого блока (`formSink`: канва рисует её), иначе из
 * сохранённого блока. Путь не найден или блок не секция — просто выбор блока.
 */

import { usePreviewBridge, type PreviewBridge } from "./usePreviewBridge";
import { useBuilder } from "../../components/site-builder/builder-context";
import { isSectionBlock } from "../../components/site-pages/block-meta";
import { useNotify } from "../../components/ui/NotifyProvider";
import type { SitePageBlock } from "../../lib/api-site-pages";
import { blocksFullText } from "../../lib/site-builder/block-limits";
import { elementIdAtPath } from "../../lib/site-builder/section-edit";
import type { PreviewMode } from "../../lib/site-view/preview";

interface Options {
  /** Что на канве: черновик или «Сейчас на сайте» (с учётом чужого источника). */
  mode: PreviewMode;
  /** Канва с панелью инструментов (на телефоне под шторкой — без) — повод подсветить заново. */
  refocusKey: boolean;
}

export function useCanvasBridge({ mode, refocusKey }: Options): PreviewBridge {
  const b = useBuilder();
  const notify = useNotify();
  const { selection, override, pages } = b;
  const page = pages.page;
  // Правит канву только черновик своей страницы: «Сейчас на сайте» и снимок дизайна/версии — смотрим.
  const editable = mode === "draft" && !override;

  const selectBlock = (block: SitePageBlock) => b.select({ sel: { kind: "block", id: block.id }, panel: null });

  /** Id элемента по пути: из несохранённой формы этого блока, иначе из сохранённого. */
  const elementIdOf = (block: SitePageBlock, path: string): string | null => {
    const sink = b.formSink.current;
    if (sink && sink.blockId === block.id && sink.dirty) return sink.elementIdAt(path);
    return isSectionBlock(block) ? elementIdAtPath(block.payload, path) : null;
  };

  return usePreviewBridge({
    iframeRef: b.iframeRef,
    blocks: page?.blocks ?? null,
    actions: b.blocks,
    onSelect: (block) => void selectBlock(block),
    onElementSelect: (block, path) => {
      // Id — до смены выбора: форма другого блока после неё уже закрыта.
      const elementId = elementIdOf(block, path);
      void selectBlock(block).then((ok) => {
        // Несохранённую форму не бросили — выбор не сменился, элемент не трогаем.
        if (ok && elementId) b.setElement({ blockId: block.id, elementId });
      });
    },
    onAddHere: (after) => {
      if (!editable || !b.canEditPages) return;
      const full = blocksFullText(page);
      if (full) notify.info(full);
      else b.openLibrary(after);
    },
    selectedBlockId: editable && selection.sel.kind === "block" ? selection.sel.id : null,
    refocusKey,
  });
}
